import {ErrorHandler, inject, Injectable} from '@angular/core';
import {NavigationEnd, Params, Router} from "@angular/router";
import {filter} from "rxjs/operators";
import {AssistantAction, AssistantSuggestion, AssistantTextPart} from "../interfaces/assistant";

/** Обробник команди помічника, який реєструє сторінка */
export type AssistantActionHandler = (action: AssistantAction) => void;

/**
 * Сторінки кабінету, які помічник може відкрити (як CabinetAssistantService::PAGES на беку).
 * Службові deleted, site_blocked, анкета survey і все поза кабінетом - ні.
 */
const CABINET_ROUTE = /^\/(account|good\/\d+|basket|basket_confirm|orders_history_new|orders_history_nalojki|orders|favorites|personal|polls)$/;

/** Картка товару публічного сайту (/catalog/<slug>-p<id>) - у кабінеті це /good/<id> */
const CATALOG_PRODUCT = /^\/catalog\/[^/]*-p(\d+)\/?$/;

/** Хости, посилання на які ведуть у кабінет: бек пише адреси проду, на стенді - поточний хост */
const OWN_HOSTS = ['maslotochka.com', 'www.maslotochka.com'];

/** Адреса в тексті відповіді: до пробілу чи лапок; розділові знаки в кінці - не частина адреси */
const URL_IN_TEXT = /https?:\/\/[^\s<>"'«»]+/g;
const URL_TRAILING_PUNCTUATION = /[.,;:!?)\]}]+$/;

/** Сторінки історії замовлень: на них open_order розкриває замовлення, show_track_status відкриває вікно статусу */
const ORDER_ROUTES = ['/orders_history_new', '/orders_history_nalojki'];

/** Де open_order розкриває замовлення: історія і заявки на повернення (/orders - вікно вибору товарів для повернення) */
const OPEN_ORDER_ROUTES = [...ORDER_ROUTES, '/orders'];

/** Кнопки suggest (як CabinetAssistantService::toolSuggest): скільки й довжина напису та тексту, що надсилається */
const SUGGEST_MAX = 6;
const SUGGEST_TEXT_MAX = 60;
const SUGGEST_SEND_MAX = 300;

/** Що підсвічує highlight і на яких сторінках: товари (pack_id) - каталог і кошик, замовлення - історія */
const HIGHLIGHT_ROUTES: {[target: string]: string[]} = {
  good: ['/account', '/basket'],
  order: ORDER_ROUTES,
};

/** Якорі картки товару (navigate.anchor) */
const GOOD_ANCHORS = ['analogs'];

/** Фільтри каталогу set_filter (як CabinetAssistantService::catalogFilters): id і списки значень (в адресі - через кому) */
const FILTER_IDS = ['brand_id', 'category_id'];
const FILTER_LISTS = ['liters', 'viscosity', 'approvals', 'class_api', 'class_acea', 'class_ilsac', 'class_jaso', 'composition'];

/** Скільки команда сторінки чекає, поки її сторінка відкриється й зареєструє обробник, мс */
const PENDING_TTL = 15000;

/** Команда сторінки, що чекає своєї сторінки */
interface PendingAction {
  type: string;
  route: string;
  action: AssistantAction;
  expires: number;
}

/**
 * Картки #963, #965, #966: команди вікну чату з відповіді помічника (actions) - виконуються одразу, без підтверджень,
 * по черзі: команда сторінки чекає, поки завершиться перехід, який зробила попередня.
 *
 * Перехід виконує сам сервіс (Router.navigate) і лише на сторінку кабінету: бек маршрут уже перевірив, але вікно
 * перевіряє ще раз - помічник ніколи не відкриває нічого поза кабінетом:
 * - navigate - сторінка кабінету; якір картки товару (anchor) сервіс передає її обробнику anchor;
 * - set_filter - каталог /account з пошуком і фільтрами (уже відкритий каталог перечитує товари без перезавантаження);
 * - open_order - сторінка історії замовлень чи заявок на повернення, далі її обробник розкриває замовлення;
 * - show_track_status - сторінка історії замовлень, далі її обробник відкриває вікно статусу замовлення.
 * Решту виконують сторінки: реєструють обробник на час свого життя (register у ngOnInit, зняття - у ngOnDestroy):
 * highlight (підсвітити рядки), refresh_basket (перечитати кошик), open_order, show_track_status, anchor. Команду
 * отримує лише обробник тієї сторінки, для якої вона (route = поточний шлях); сторінка ще відкривається - команда чекає
 * до PENDING_TTL. refresh_basket - лише кошику, відкритому ще до відповіді (щойно відкритий і так читає свіжі дані).
 * suggest - кнопки під відповіддю: їх бере вікно чату (suggestions), тут команда нічого не робить. Незнайомий type
 * пропускаємо.
 */
@Injectable({
  providedIn: 'root'
})
export class AssistantActionsService {
  private router = inject(Router);
  private errorHandler = inject(ErrorHandler);

  private handlers = new Map<string, Set<AssistantActionHandler>>();
  private pending: PendingAction[] = [];

  constructor() {
    // Сторінка, перевикористана під новий маршрут (картка іншого товару), обробник не реєструє вдруге
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => this.flushSoon());
  }

  /** Зареєструвати обробник типу команди; повертає функцію, що його знімає */
  register(type: string, handler: AssistantActionHandler): () => void {
    if (!this.handlers.has(type)) {
      this.handlers.set(type, new Set());
    }

    this.handlers.get(type)!.add(handler);
    this.flushSoon();

    return () => {
      this.handlers.get(type)?.delete(handler);
    };
  }

  /** Виконати команди з відповіді - по черзі; збій однієї не зупиняє решту */
  run(actions: unknown): void {
    if (!Array.isArray(actions)) {
      return;
    }

    // Нова відповідь: команди попередньої, що досі чекали сторінку, вже не потрібні
    this.pending = [];

    // Яка сторінка була відкрита, коли прийшла відповідь (refresh_basket - лише для вже відкритого кошика)
    const startPath = this.currentPath();

    actions.reduce(
      (chain: Promise<void>, action: AssistantAction) => chain
        .then(() => this.runOne(action, startPath))
        .catch((e) => this.errorHandler.handleError(e)),
      Promise.resolve()
    );
  }

  /**
   * Маршрут кабінету з адреси чи команди - або null, якщо це не сторінка кабінету.
   * Параметри - лише в каталогу (/account) і лише ті, що бек дозволяє помічнику.
   */
  cabinetRoute(route: unknown, query?: unknown): {route: string; query?: Params} | null {
    if (typeof route !== 'string') {
      return null;
    }

    const product = route.match(CATALOG_PRODUCT);
    const path = product ? '/good/' + product[1] : route;

    if (!CABINET_ROUTE.test(path)) {
      return null;
    }

    const params = path === '/account' ? this.accountQuery(query) : null;

    return params ? {route: path, query: params} : {route: path};
  }

  /**
   * Текст відповіді шматками для вікна: адреси сторінок кабінету - посиланнями всередині кабінету,
   * решта (у тому числі адреси поза кабінетом) - звичайним текстом.
   */
  textParts(text: string): AssistantTextPart[] {
    const parts: AssistantTextPart[] = [];
    let last = 0;

    for (const match of text.matchAll(URL_IN_TEXT)) {
      const href = match[0].replace(URL_TRAILING_PUNCTUATION, '');
      const target = this.linkTarget(href);
      const start = match.index ?? 0;

      if (!target) {
        continue;
      }

      if (start > last) {
        parts.push({text: text.slice(last, start)});
      }

      parts.push({text: href, ...target});
      last = start + href.length;
    }

    if (last < text.length) {
      parts.push({text: text.slice(last)});
    }

    return parts;
  }

  private async runOne(action: AssistantAction, startPath: string): Promise<void> {
    if (!action || typeof action.type !== 'string') {
      return;
    }

    switch (action.type) {
      case 'navigate':
        return this.navigate(action);
      case 'set_filter':
        return this.setFilter(action);
      case 'open_order':
        return this.openOrder(action);
      case 'show_track_status':
        return this.showTrackStatus(action);
      case 'highlight':
        return this.highlight(action);
      case 'refresh_basket':
        // Кошик відкриває ця ж відповідь - він і так прочитає свіжі дані, другий запит не потрібен
        if (startPath === '/basket') {
          this.deliver('refresh_basket', '/basket', action, false);
        }
        return;
    }
  }

  private async navigate(action: AssistantAction): Promise<void> {
    const target = this.cabinetRoute(action.route, action.query);

    if (!target) {
      return;
    }

    await this.router.navigate([target.route], target.query ? {queryParams: target.query} : {});

    // Якір картки товару: картка прокрутить до блоку, коли покаже цей товар
    if (target.route.startsWith('/good/') && typeof action.anchor === 'string' && GOOD_ANCHORS.includes(action.anchor)) {
      this.deliver('anchor', target.route, {type: 'anchor', route: target.route, anchor: action.anchor});
    }
  }

  /**
   * Пошук і фільтри каталогу. Каталог бере їх з адреси (account.component.ts, parseUrlParams), тож це перехід на
   * /account з новими параметрами: відкритий каталог перечитує товари без перезавантаження, інакше каталог відкривається.
   * Параметрів, яких немає в команді, в адресі теж немає (фільтри замінюють поточні); прапорці наявності без значення
   * лишаються як були.
   */
  private async setFilter(action: AssistantAction): Promise<void> {
    const params = {...(this.accountQuery(action.query) || {}), ...this.accountFilters(action.filters)};

    await this.router.navigate(['/account'], Object.keys(params).length ? {queryParams: params} : {});
  }

  /**
   * Кнопки-уточнення з відповіді (команда suggest, остання з них): лише непорожні напис і текст, без повторів, не більше
   * SUGGEST_MAX. Натиснута кнопка - звичайне повідомлення клієнта (send), бек перевіряє його, як будь-яке інше.
   */
  suggestions(actions: unknown): AssistantSuggestion[] {
    if (!Array.isArray(actions)) {
      return [];
    }

    const suggest = actions.filter((action) => action && action.type === 'suggest' && Array.isArray(action.options)).pop();
    const options: AssistantSuggestion[] = [];

    for (const option of (suggest ? suggest.options : [])) {
      const text = option && typeof option.text === 'string' ? option.text.replace(/\s+/g, ' ').trim().slice(0, SUGGEST_TEXT_MAX) : '';
      const send = option && typeof option.send === 'string' && option.send.trim() !== ''
        ? option.send.replace(/\s+/g, ' ').trim().slice(0, SUGGEST_SEND_MAX)
        : text;

      if (text !== '' && !options.some((known) => known.send === send)) {
        options.push({text, send});
      }

      if (options.length >= SUGGEST_MAX) {
        break;
      }
    }

    return options;
  }

  /** Відкрити сторінку замовлення (її вибрав бек: історія чи заявки на повернення), а розкриє замовлення обробник сторінки */
  private async openOrder(action: AssistantAction): Promise<void> {
    const orderId = Number(action.order_id);
    const route = this.orderRoute(action, OPEN_ORDER_ROUTES);

    if (!Number.isInteger(orderId) || orderId <= 0 || !route) {
      return;
    }

    if (this.currentPath() !== route) {
      await this.router.navigate([route]);
    }

    this.deliver('open_order', route, {...action, route});
  }

  /** Вікно статусу замовлення: сторінка історії (її вибрав бек), а вікно відкриє обробник сторінки, коли покаже замовлення */
  private async showTrackStatus(action: AssistantAction): Promise<void> {
    const orderId = Number(action.order_id);
    const route = this.orderRoute(action, ORDER_ROUTES);

    if (!Number.isInteger(orderId) || orderId <= 0 || !route) {
      return;
    }

    if (this.currentPath() !== route) {
      await this.router.navigate([route]);
    }

    this.deliver('show_track_status', route, action);
  }

  /** Маршрут команди замовлення - route або page (без «/»), лише зі списку allowed; інакше null */
  private orderRoute(action: AssistantAction, allowed: string[]): string | null {
    const route = typeof action.route === 'string' ? action.route : (typeof action.page === 'string' ? '/' + action.page : '');

    return allowed.includes(route) ? route : null;
  }

  private highlight(action: AssistantAction): void {
    const routes = typeof action.target === 'string' ? HIGHLIGHT_ROUTES[action.target] : undefined;

    if (!routes || typeof action.route !== 'string' || !routes.includes(action.route) || !Array.isArray(action.ids)) {
      return;
    }

    this.deliver('highlight', action.route, action);
  }

  /** Передати команду обробнику сторінки route; сторінка ще не відкрита - чекати (wait) або пропустити */
  private deliver(type: string, route: string, action: AssistantAction, wait = true): void {
    if (this.handOver(type, route, action) || !wait) {
      return;
    }

    this.pending.push({type, route, action, expires: Date.now() + PENDING_TTL});
  }

  /** Віддати команду, якщо відкрита саме її сторінка і та вже зареєструвала обробник */
  private handOver(type: string, route: string, action: AssistantAction): boolean {
    const handlers = this.handlers.get(type);

    if (this.currentPath() !== route || !handlers || !handlers.size) {
      return false;
    }

    handlers.forEach((handler) => handler(action));

    return true;
  }

  // Після реєстрації обробника чи переходу: сторінка вже відкрита, але обробник могла зареєструвати щойно
  private flushSoon(): void {
    if (this.pending.length) {
      setTimeout(() => this.flushPending());
    }
  }

  private flushPending(): void {
    const now = Date.now();

    this.pending = this.pending.filter((item) => {
      if (item.expires < now) {
        return false;
      }

      try {
        return !this.handOver(item.type, item.route, item.action);
      } catch (e) {
        this.errorHandler.handleError(e);
        return false;
      }
    });
  }

  /** Шлях поточної сторінки без параметрів і якоря */
  private currentPath(): string {
    return this.router.url.split(/[?#]/)[0];
  }

  /** Посилання з тексту: лише наш сайт і лише сторінка кабінету */
  private linkTarget(href: string): {route: string; query?: Params} | null {
    let url: URL;

    try {
      url = new URL(href);
    } catch (e) {
      return null;
    }

    if (url.hostname !== window.location.hostname && OWN_HOSTS.indexOf(url.hostname) === -1) {
      return null;
    }

    const query: Params = {};
    url.searchParams.forEach((value, key) => {
      query[key] = value;
    });

    return this.cabinetRoute(url.pathname, query);
  }

  /** Параметри каталогу, які бек дозволяє помічнику (toolOpenPage): пошук, режим пошуку, два прапорці */
  private accountQuery(query: unknown): Params | null {
    if (!query || typeof query !== 'object') {
      return null;
    }

    const raw = query as Params;
    const params: Params = {};

    const search = typeof raw['search'] === 'string' ? raw['search'].trim() : '';
    if (search !== '') {
      params['search'] = search;

      // ai - платний ІІ-пошук, помічник його не вмикає
      if (raw['searchMode'] === 'default' || raw['searchMode'] === 'exact') {
        params['searchMode'] = raw['searchMode'];
      }
    }

    ['haveGoods', 'includePartners'].forEach((flag) => {
      const value = raw[flag];

      if ((typeof value === 'number' || typeof value === 'string') && (String(value) === '0' || String(value) === '1')) {
        params[flag] = Number(value);
      }
    });

    return Object.keys(params).length ? params : null;
  }

  /**
   * Фільтри каталогу з set_filter (бек уже звірив їх із довідниками каталогу) - параметрами адреси /account:
   * бренд і категорія - числом, характеристики - значення через кому (як їх ставить меню каталогу).
   */
  private accountFilters(filters: unknown): Params {
    const params: Params = {};

    if (!filters || typeof filters !== 'object') {
      return params;
    }

    const raw = filters as Params;

    FILTER_IDS.forEach((key) => {
      const value = Number(raw[key]);

      if (Number.isInteger(value) && value > 0) {
        params[key] = value;
      }
    });

    FILTER_LISTS.forEach((key) => {
      const list: unknown[] = Array.isArray(raw[key]) ? raw[key] : [];
      // Кома - роздільник значень в адресі, тож значення з комою передати не можна (меню каталогу їх теж не ставить)
      const values = list
        .filter((value) => typeof value === 'string' || typeof value === 'number')
        .map((value) => String(value).trim())
        .filter((value) => value !== '' && value.length <= 100 && !value.includes(','));

      if (values.length) {
        params[key] = values.join(',');
      }
    });

    return params;
  }
}
